import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Truck, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, cartProducts, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');

  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    phone: user?.phone || '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const getProduct = (productId) => cartProducts.find(p => p.id === productId);

  const subtotal = getCartTotal();
  const total = subtotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to place order');
      return;
    }

    // Mandatory field validation
    if (!formData.city.trim()) {
      toast.error('Please enter your City');
      document.getElementById('city')?.focus();
      return;
    }
    if (!formData.state.trim()) {
      toast.error('Please enter your State');
      document.getElementById('state')?.focus();
      return;
    }
    if (!formData.pincode.trim()) {
      toast.error('Please enter your Pincode');
      document.getElementById('pincode')?.focus();
      return;
    }
    if (!/^[0-9]{6}$/.test(formData.pincode.trim())) {
      toast.error('Pincode must be exactly 6 digits');
      document.getElementById('pincode')?.focus();
      return;
    }

    setLoading(true);

    try {
      const orderItems = cart.items.map(item => {
        const product = getProduct(item.product_id);
        const variant = product.variants[item.variant_index];
        return {
          product_id: item.product_id,
          product_name: product.name,
          variant_size: variant.size,
          price: variant.price,
          quantity: item.quantity,
          image_url: product.images?.[0]
        };
      });

      const response = await axios.post(`${API_URL}/orders`, {
        shipping_address: formData,
        items: orderItems,
        subtotal,
        delivery_charge: 0,
        total
      });

      setOrderNumber(response.data.order_number);
      setOrderPlaced(true);
      await clearCart();
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-stone-100">
            <div className="h-20 w-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-primary" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
              Order Placed Successfully!
            </h1>
            <p className="text-muted-foreground mb-4">
              Thank you for your order. We'll deliver your organic goodness soon.
            </p>
            <div className="bg-muted/50 rounded-xl p-4 mb-6">
              <p className="text-sm text-muted-foreground">Order Number</p>
              <p className="font-semibold text-primary text-lg">{orderNumber}</p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/orders')}
                className="w-full bg-primary hover:bg-primary/90 rounded-full"
              >
                View My Orders
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full rounded-full"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form id="checkout-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Shipping Address */}
              <div className="bg-white rounded-2xl border border-stone-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Truck className="h-5 w-5 text-primary" />
                  <h2 className="font-semibold text-lg text-stone-900">Shipping Address</h2>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="bg-stone-50/50"
                      data-testid="checkout-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-stone-50/50"
                      data-testid="checkout-phone"
                    />
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label htmlFor="address_line1">Address Line 1 *</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={formData.address_line1}
                      onChange={handleChange}
                      required
                      placeholder="House/Flat No., Building, Street"
                      className="bg-stone-50/50"
                      data-testid="checkout-address1"
                    />
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={formData.address_line2}
                      onChange={handleChange}
                      placeholder="Area, Landmark"
                      className="bg-stone-50/50"
                      data-testid="checkout-address2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="bg-stone-50/50"
                      data-testid="checkout-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="bg-stone-50/50"
                      data-testid="checkout-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{6}"
                      maxLength={6}
                      className="bg-stone-50/50"
                      data-testid="checkout-pincode"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl border border-stone-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <h2 className="font-semibold text-lg text-stone-900">Payment Method</h2>
                </div>

                <div className="p-4 bg-primary/5 rounded-xl border-2 border-primary">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-primary rounded-full flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">Cash on Delivery</p>
                      <p className="text-sm text-muted-foreground">Pay when you receive your order</p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full lg:hidden bg-primary hover:bg-primary/90 rounded-full py-6 text-lg"
                data-testid="place-order-mobile"
              >
                {loading ? 'Placing Order...' : `Place Order - ₹${total}`}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-stone-100 p-6 sticky top-32">
              <h2 className="font-semibold text-lg text-stone-900 mb-6">Order Summary</h2>

              {/* Items */}
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {cart.items.map((item, index) => {
                  const product = getProduct(item.product_id);
                  if (!product) return null;
                  const variant = product.variants[item.variant_index];
                  
                  return (
                    <div key={index} className="flex gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-stone-50 flex-shrink-0">
                        <img
                          src={product.images?.[0] || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=100'}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 line-clamp-1">
                          {product.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {variant.size} × {item.quantity}
                        </p>
                      </div>
                      <p className="text-sm font-medium">
                        ₹{variant.price * item.quantity}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-stone-100 mt-4 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  * Delivery charges applicable
                </p>
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary">₹{total}</span>
                </div>
              </div>

              <Button
                type="submit"
                form="checkout-form"
                disabled={loading}
                className="w-full mt-6 bg-primary hover:bg-primary/90 rounded-full py-6 hidden lg:flex"
                data-testid="place-order"
              >
                {loading ? 'Placing Order...' : 'Place Order'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
